import React, { useMemo } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { IRow, IRowMeta } from '../../../redux/slices/oldBuilder';
import MeasureRowHeader from './MeasureRowHeader';
import MeasureRowContent from './MeasureRowContent';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../ActionsVisibilityContext';
import { useAppSelector } from '../../../redux/hooks';
import { useDndEntityInternalContext } from '../../DndEntity';
import DndDroppableZone from '../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../DndEntity/utils';
import DropzoneArea from '../../DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  ICohortEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../DndEntity/validation';
import { getCharacterByIncrementedIndex } from '../../../utils/character';
import { rowSelector } from '../../../redux/selectors/builder';
import { isEmptyEventConditions } from '../../../utils/builder';

interface IProps {
  rowMeta: IRowMeta;
  cohortRowMeta: IRowMeta;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  hideContent?: boolean;
}

const BuilderMeasure = ({
  rowMeta,
  cohortRowMeta,
  dragHandleProps,
  hideContent,
}: IProps) => {
  const { showActions, hideActions } = useActionsVisibilityContext();

  const currentRow: IRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );

  const measureFunctions = useAppSelector(
    ({ builder }) => builder.defaultMeasureFunctions,
  );
  const measureCharacter = getCharacterByIncrementedIndex(
    'A',
    rowMeta.rowIndex,
  );

  const { activeItem } = useDndEntityInternalContext();

  const showAddCohortFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (
      data &&
      !currentRow.cohortFilter?.cohortId &&
      isEmptyEventConditions(currentRow.cohortFilter?.eventConditions)
    ) {
      return (
        data?.__typename === 'TagLeafSimpleCohort' ||
        data?.__typename === 'TagLeafCompoundCohort'
      );
    }

    return false;
  }, [activeItem, currentRow]);

  const cohortDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Cohorts,
    rowMeta,
    'Cohort-Filter',
  );

  const showAddMeasureCohortFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (
      data &&
      !currentRow.cohortFilter?.cohortId &&
      !currentRow.cohortFilter?.analysisId &&
      !currentRow.cohortFilter?.measureId &&
      isEmptyEventConditions(currentRow.cohortFilter?.eventConditions) &&
      !isEmptyEventConditions(currentRow.eventConditions)
    ) {
      return (
        data.__typename === 'TagLeafSimpleMeasure' ||
        data.__typename === 'TagLeafCompoundMeasure'
      );
    }

    return false;
  }, [activeItem, currentRow]);

  const measureCohortDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Measures,
    rowMeta,
    'Cohort-Filter',
  );

  return (
    <div onMouseEnter={showActions} onMouseLeave={hideActions}>
      <MeasureRowHeader
        rowMeta={rowMeta}
        cohortRowMeta={cohortRowMeta}
        dragHandleProps={dragHandleProps}
      />
      {!hideContent && (
        <MeasureRowContent
          rowMeta={rowMeta}
          measureFunctions={measureFunctions}
        />
      )}
      {showAddCohortFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Cohorts]}
          id={cohortDropzoneId}
          data={
            {
              id: cohortDropzoneId,
              type: EntityDropzoneDataTypeEnum.Cohorts,
              rowMeta: cohortRowMeta,
            } satisfies ICohortEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">
            + Add Cohort Filter for Measure {measureCharacter}
          </DropzoneArea>
        </DndDroppableZone>
      )}
      {showAddMeasureCohortFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Measures]}
          id={measureCohortDropzoneId}
          data={
            {
              id: measureCohortDropzoneId,
              type: EntityDropzoneDataTypeEnum.Measures,
              rowMeta: cohortRowMeta,
            } satisfies IMeasureEntityDropzoneDataType
          }
          isEmbedded
        >
          <DropzoneArea size="large">
            + Add Measure Cohort Filter for Measure {measureCharacter}
          </DropzoneArea>
        </DndDroppableZone>
      )}
    </div>
  );
};

export default withActionsVisibilityContext(BuilderMeasure);
